import { useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@multaqa/ui/components/ui/dropdown-menu'
import { Link } from '@tanstack/react-router'
import { EllipsisIcon, type LucideIcon } from 'lucide-react'
import { Fragment } from 'react'

type ActionBase = {
  id: string
  label: string
  icon?: LucideIcon
  iconDirectional?: boolean
  variant?: 'primary' | 'outline' | 'destructive'
  primary?: boolean
  hidden?: boolean
  disabled?: boolean
  loading?: boolean
  title?: string
  current?: boolean
  testId?: string
}

type ActionLink = {
  to: string
  params?: Record<string, string>
  search?: Record<string, string | number>
}

export type RecordHeaderAction = ActionBase &
  (
    | { items: RecordHeaderAction[]; onSelect?: never; link?: never }
    | { onSelect: () => void; items?: never; link?: never }
    | { link: ActionLink; items?: never; onSelect?: never }
  )

export function RecordHeaderActions({
  actions,
  menuTestId,
}: {
  actions: RecordHeaderAction[]
  menuTestId?: string
}) {
  const visible = actions.filter((action) => !action.hidden)
  const primary = visible.filter((action) => action.primary)
  const menuActions = visible.filter((action) => !action.primary)
  const orderedMenuActions = [
    ...menuActions.filter((action) => action.variant !== 'destructive'),
    ...menuActions.filter((action) => action.variant === 'destructive'),
  ]
  if (visible.length === 0) return null

  return (
    <div
      className="flex max-w-full flex-wrap items-center justify-end gap-2"
      data-testid="record-header-actions"
    >
      {primary.map((action) => (
        <PrimaryAction key={action.id} action={action} />
      ))}
      {orderedMenuActions.length > 0 ? (
        <ActionMenu
          actions={orderedMenuActions}
          menuTestId={menuTestId}
          destructiveStart={orderedMenuActions.findIndex(
            (action) => action.variant === 'destructive',
          )}
        />
      ) : null}
    </div>
  )
}

function PrimaryAction({ action }: { action: RecordHeaderAction }) {
  const Icon = action.icon
  const variant = action.variant === 'primary' ? 'primary' : (action.variant ?? 'outline')
  const unavailable = action.disabled || action.loading

  return action.link ? (
    <Button asChild variant={variant} title={action.title} data-testid={action.testId}>
      <Link
        to={action.link.to as never}
        params={action.link.params as never}
        search={action.link.search as never}
        aria-disabled={unavailable || undefined}
        tabIndex={unavailable ? -1 : undefined}
        onClick={unavailable ? (event) => event.preventDefault() : undefined}
      >
        {Icon ? (
          <Icon
            className={action.iconDirectional ? 'size-3.5 rtl:-scale-x-100' : 'size-3.5'}
            aria-hidden="true"
          />
        ) : null}
        {action.label}
      </Link>
    </Button>
  ) : (
    <Button
      type="button"
      variant={variant}
      disabled={action.disabled}
      loading={action.loading}
      title={action.title}
      onClick={action.onSelect}
      data-testid={action.testId}
    >
      {Icon ? (
        <Icon
          className={action.iconDirectional ? 'size-3.5 rtl:-scale-x-100' : 'size-3.5'}
          aria-hidden="true"
        />
      ) : null}
      {action.label}
    </Button>
  )
}

function ActionMenu({
  actions,
  destructiveStart,
  menuTestId,
}: {
  actions: RecordHeaderAction[]
  destructiveStart: number
  menuTestId?: string
}) {
  const { t } = useTranslation()
  const { direction } = useLocale()

  return (
    <DropdownMenu dir={direction}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={menuTestId ?? 'record-header-actions-menu'}
        >
          <EllipsisIcon className="size-3.5" aria-hidden="true" />
          {t('common:action.actions')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action, index) => {
          const Icon = action.icon
          const destructive = action.variant === 'destructive'
          const item = action.items ? (
            <DropdownMenuSub key={action.id}>
              <DropdownMenuSubTrigger
                data-testid={action.testId}
                disabled={action.disabled || action.loading}
                title={action.title}
              >
                {Icon ? (
                  <Icon
                    className={action.iconDirectional ? 'rtl:-scale-x-100' : undefined}
                    aria-hidden="true"
                  />
                ) : null}
                {action.label}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {action.items
                  .filter((child) => !child.hidden)
                  .map((child) => {
                    const ChildIcon = child.icon
                    return child.link ? (
                      <DropdownMenuItem
                        key={child.id}
                        asChild
                        disabled={child.disabled || child.loading}
                      >
                        <Link
                          to={child.link.to as never}
                          params={child.link.params as never}
                          search={child.link.search as never}
                          aria-current={child.current ? 'true' : undefined}
                          data-testid={child.testId}
                        >
                          {ChildIcon ? (
                            <ChildIcon
                              className={child.iconDirectional ? 'rtl:-scale-x-100' : undefined}
                              aria-hidden="true"
                            />
                          ) : null}
                          {child.label}
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        key={child.id}
                        disabled={child.disabled || child.loading}
                        onSelect={child.onSelect}
                        aria-current={child.current ? 'true' : undefined}
                        data-testid={child.testId}
                      >
                        {ChildIcon ? (
                          <ChildIcon
                            className={child.iconDirectional ? 'rtl:-scale-x-100' : undefined}
                            aria-hidden="true"
                          />
                        ) : null}
                        {child.label}
                      </DropdownMenuItem>
                    )
                  })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem
              key={action.id}
              variant={destructive ? 'destructive' : undefined}
              disabled={action.disabled || action.loading}
              onSelect={action.onSelect}
              data-testid={action.testId}
              title={action.title}
            >
              {Icon ? (
                <Icon
                  className={action.iconDirectional ? 'rtl:-scale-x-100' : undefined}
                  aria-hidden="true"
                />
              ) : null}
              {action.label}
            </DropdownMenuItem>
          )

          return (
            <Fragment key={action.id}>
              {index === destructiveStart && index > 0 ? <DropdownMenuSeparator /> : null}
              {action.link ? (
                <DropdownMenuItem
                  asChild
                  variant={destructive ? 'destructive' : undefined}
                  disabled={action.disabled || action.loading}
                  title={action.title}
                >
                  <Link
                    to={action.link.to as never}
                    params={action.link.params as never}
                    search={action.link.search as never}
                    title={action.title}
                    data-testid={action.testId}
                  >
                    {Icon ? (
                      <Icon
                        className={action.iconDirectional ? 'rtl:-scale-x-100' : undefined}
                        aria-hidden="true"
                      />
                    ) : null}
                    {action.label}
                  </Link>
                </DropdownMenuItem>
              ) : (
                item
              )}
            </Fragment>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
